import { axiosApiInstance } from "../axios";
import { makePostApiCall } from "./makeApiCall";

const getSelectValue = (field) => field?.value ?? null;

export function hasNewImageFile(value) {
  return Array.isArray(value) && value.length > 0 && value[0] instanceof File;
}

export function needsFormDataUpload(payload) {
  return (
    hasNewImageFile(payload.product_image) ||
    hasNewImageFile(payload.product_image_2) ||
    hasNewImageFile(payload.nutrition_facts)
  );
}

export function buildProductSavePayload(values, { isEdit = false } = {}) {
  const taxableValue = values.taxable?.value ?? null;

  const payload = {
    name: values.name,
    name_ch: values.name_ch,
    plu: values.plu,
    upc_code: values.upc_code,
    description_1: values.description_1,
    description_2: values.description_2,
    description_3: values.description_3,
    disclaimer: values.disclaimer,
    standard_weight: getSelectValue(values.standard_weight),
    stock_unit_of_measure: getSelectValue(values.stock_unit_of_measure),
    selling_unit_of_measure: getSelectValue(values.selling_unit_of_measure),
    piece_count: values.piece_count,
    upper_set_point: values.upper_set_point,
    lower_set_point: values.lower_set_point,
    is_new: getSelectValue(values.is_new),
    class: getSelectValue(values.class),
    category: getSelectValue(values.category),
    box_type: getSelectValue(values.box_type),
    organic: getSelectValue(values.organic),
    allergens: values.allergens ?? [],
    shelf_life: values.shelf_life,
    colour: getSelectValue(values.colour),
    odour: getSelectValue(values.odour),
    taste: getSelectValue(values.taste),
    warehouse_id: getSelectValue(values.warehouse_id),
    warehouse_location_id: getSelectValue(values.warehouse_location_id),
    reorder_limit: values.reorder_limit,
    storage_condition: values.storage_condition,
    packaging_size: values.packaging_size,
    packaging_material: values.packaging_material,
    directions_for_use: values.directions_for_use,
    country_of_origin: values.country_of_origin,
    hot_key_1: values.hot_key_1,
    hot_key_2: values.hot_key_2,
    food_safety: values.food_safety,
    certifications: values.certifications,
    ingredients_en: values.ingredients_en,
    price_1: values.price_1,
    price_2: values.price_2,
    price_3: values.price_3,
    price_4: values.price_4,
    unit_cost: values.unit_cost,
    taxable: taxableValue,
    taxes: taxableValue === 2 ? (values.taxes ?? []) : [],
    label_graphic: getSelectValue(values.label_graphic),
  };

  if (isEdit) {
    payload.require_best_before_on_receiving = getSelectValue(
      values.require_best_before_on_receiving,
    );
    if (values.id != null) {
      payload.id = values.id;
    }
  }

  if (hasNewImageFile(values.product_image) || values.product_image === "") {
    payload.product_image = values.product_image;
  }
  if (hasNewImageFile(values.product_image_2) || values.product_image_2 === "") {
    payload.product_image_2 = values.product_image_2;
  }
  if (hasNewImageFile(values.nutrition_facts) || values.nutrition_facts === "") {
    payload.nutrition_facts = values.nutrition_facts;
  }

  return payload;
}

function appendFormDataValue(fd, key, value) {
  if (value === null || value === undefined) {
    return;
  }

  if (hasNewImageFile(value)) {
    fd.append(key, value[0]);
    return;
  }

  if (Array.isArray(value)) {
    if (
      value.length > 0 &&
      typeof value[0] === "object" &&
      value[0] !== null &&
      "value" in value[0]
    ) {
      value.forEach((item, index) => {
        fd.append(`${key}[${index}][value]`, item.value);
        if (item.label != null) {
          fd.append(`${key}[${index}][label]`, item.label);
        }
      });
      return;
    }
    value.forEach((item, index) => {
      fd.append(`${key}[${index}]`, item);
    });
    return;
  }

  fd.append(key, value);
}

function buildProductFormData(payload) {
  const fd = new FormData();
  const user_id = localStorage.getItem("id") * 1;
  const user_name = localStorage.getItem("name");

  Object.entries(payload).forEach(([key, value]) => {
    appendFormDataValue(fd, key, value);
  });

  fd.append("user_id", user_id);
  fd.append("user_name", user_name);

  return fd;
}

export async function postProductSave(endPoint, payload) {
  if (!needsFormDataUpload(payload)) {
    const jsonPayload = { ...payload };
    delete jsonPayload.product_image_url;
    delete jsonPayload.product_image_2_url;
    delete jsonPayload.nutrition_facts_url;

    if (!hasNewImageFile(jsonPayload.product_image) && jsonPayload.product_image == null) {
      delete jsonPayload.product_image;
    }
    if (!hasNewImageFile(jsonPayload.product_image_2) && jsonPayload.product_image_2 == null) {
      delete jsonPayload.product_image_2;
    }
    if (!hasNewImageFile(jsonPayload.nutrition_facts) && jsonPayload.nutrition_facts == null) {
      delete jsonPayload.nutrition_facts;
    }

    return makePostApiCall(endPoint, jsonPayload);
  }

  const fd = buildProductFormData(payload);

  return axiosApiInstance
    .post(endPoint, fd, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("authToken"),
      },
    })
    .then((response) => ({
      success: true,
      data: response.data,
    }))
    .catch((err) => {
      if (!err) {
        return {
          success: false,
          data: {
            title: "Error",
            subtitle: "We could not complete the action.",
            errorStatus: 500,
          },
          errorStatus: 500,
        };
      }

      if (!err.response) {
        return {
          success: false,
          data: {
            title: "Error",
            message: "We could not complete the action.",
            errorStatus: 500,
          },
          errorStatus: 500,
        };
      }

      const emptyData = {
        data: {
          title: "Error",
          message: "We could not complete the action.",
          errorStatus: err.response.status,
        },
      };

      if (err.response.data) {
        return {
          success: false,
          data: err.response.data,
          errorStatus: err.response.status,
        };
      }

      return {
        success: false,
        data: emptyData,
        errorStatus: err.response.status,
      };
    });
}
